// #324: das Vokabular rund um die Überarbeitung („zur Überarbeitung zurück", #323).
//
// Bis #323 kannte eine Freigabe zwei Ausgänge — ja oder nein. Ein „so nicht, bitte
// nachbessern" gab es nicht, obwohl es die häufigste menschliche Antwort auf eine Vorlage
// ist. Seit #323 kann ein Gate den Lauf an einen früheren Schritt zurückschicken; damit
// bekommt jede Entscheidung eine RUNDE, und die Oberfläche muss sagen können, die
// wievielte Fassung gerade auf dem Tisch liegt.
//
// Alles hier ist PUR (keine React-Abhängigkeit) — die Regeln sind ohne Rendern prüfbar,
// und beide Nutzer (Freigabe-Karte + Zeitleiste) lesen dieselbe eine Wahrheit.

/**
 * Ist `runde` eine Angabe, auf die man einen Satz bauen darf?
 *
 * ⚠ `null` heisst „nicht ermittelbar" (Ereignis-Log nicht lesbar), `undefined` „von diesem
 * Master nicht geliefert". Beides ist KEINE 1 — eine erfundene Runde wäre eine Behauptung
 * über etwas, das der Lauf nicht weiss. Gebrochene Werte (0, -1, 1.5, NaN) kommen aus
 * derselben Ecke wie ein fehlendes Feld und werden genauso zurückhaltend behandelt.
 */
function brauchbareRunde(runde: number | null | undefined): number | null {
  return typeof runde === "number" && Number.isInteger(runde) && runde >= 1 ? runde : null;
}

/**
 * Der Runden-Zusatz „ (Runde N)" — oder leer.
 *
 * `ab` ist die Schwelle, ab der die Runde überhaupt erwähnenswert ist: bei einer Zustimmung
 * oder Ablehnung ist Runde 1 der Normalfall und stünde nur im Weg (Default 2); bei einer
 * angeforderten Überarbeitung ist schon die erste eine Aussage (`ab: 1`).
 */
export function rundenZusatz(runde: number | null | undefined, ab = 2): string {
  const n = brauchbareRunde(runde);
  return n !== null && n >= ab ? ` (Runde ${n})` : "";
}

/**
 * Die Entscheidung in Klartext, inklusive Runde: „freigegeben", „abgelehnt (Runde 2)",
 * „Zur Überarbeitung (Runde 1)".
 *
 * Ein unbekannter Ausgang wird DURCHGEREICHT (wie bisher) — er ist die Aussage eines
 * neueren Masters, und ein pauschales „abgelehnt" verwischte, was wirklich passiert ist.
 */
export function entscheidLabel(entscheid: string, runde?: number | null): string {
  const revise = entscheid === "revise";
  const wort = revise
    ? "Zur Überarbeitung"
    : entscheid === "approve"
      ? "freigegeben"
      : entscheid === "reject"
        ? "abgelehnt"
        : entscheid;
  return `${wort}${rundenZusatz(runde, revise ? 1 : 2)}`;
}

/**
 * Die Farbe des Ausgangs. Eine Überarbeitung ist KEINE Ablehnung: der Lauf geht weiter,
 * nur eben zurück in die Arbeit — rot läse sich als „gescheitert".
 */
export function entscheidKlasse(entscheid: string): string {
  if (entscheid === "approve") return "text-ok";
  if (entscheid === "revise") return "text-warn";
  return "text-danger";
}

/**
 * „Überarbeitete Fassung — Runde N" für die Freigabe-Karte, ab der ZWEITEN Vorlage.
 *
 * `null` = nichts anzeigen. Bei Runde 1 (der ersten Vorlage) gibt es nichts zu sagen; ein
 * „Runde 1"-Hinweis liesse jede gewöhnliche Freigabe nach Wiedervorlage aussehen.
 */
export function fassungLabel(runde: number | null | undefined): string | null {
  const n = brauchbareRunde(runde);
  return n !== null && n >= 2 ? `Überarbeitete Fassung — Runde ${n}` : null;
}

/**
 * Der Rahmen des Auftrags-/Begründungs-Zitats. Er folgt dem AUSGANG: eine Begründung zur
 * Ablehnung gelb zu rahmen läse sich wie „geht noch weiter", und genau das tut sie nicht.
 */
export function zitatRahmen(entscheid: string): string {
  if (entscheid === "revise") return "border-warn-border";
  if (entscheid === "reject") return "border-danger-border";
  return "border-accent-border";
}

/**
 * Was passiert, wenn ich „Zur Überarbeitung" wähle — in einem Satz, ohne Kennungen.
 *
 * Der Nebensatz zu `maxRounds` erscheint nur, wenn das Gate eine Obergrenze deklariert;
 * `null` heisst „unbegrenzt" und braucht keinen Satz. Der Ziel-Schritt kommt als KLARNAME
 * (die Kennung sagt dem Entscheider nichts).
 *
 * ⚠ `zielStepName` wird auf `typeof` geprüft, nicht bloss auf leer: liest der Master die
 * gepinnte Vorlage nicht, steht dort `null` — und `null.trim()` risse die ganze Lauf-Seite
 * ab (es gibt keine ErrorBoundary). Fremdformat ist eine ANNAHME, hier wie überall.
 */
export function ueberarbeitungSatz(u: { zielStepName: string; maxRounds: number | null }): string {
  const name = typeof u.zielStepName === "string" ? u.zielStepName.trim() : "";
  const ziel = name === "" ? "den vorherigen Schritt" : `„${name}“`;
  const grenze = brauchbareRunde(u.maxRounds)
    ? ` Höchstens ${u.maxRounds} ${u.maxRounds === 1 ? "Überarbeitung" : "Überarbeitungen"} vorgesehen.`
    : "";
  return `Der Lauf geht zurück zu ${ziel}.${grenze}`;
}

/**
 * Ist der Deckel schon überschritten — würde ein `revise` also NICHT mehr zurückführen?
 *
 * Der Master vergleicht `runde > maxRounds` (`verarbeiteUeberarbeitung`) und BLOCKIERT den
 * Lauf, statt zu werfen: die Antwort ist ein 200 mit `status:"blocked"`. Die Karte kann das
 * vorhersehen und den Knopf sperren, statt ihn in einen stillen Halt laufen zu lassen.
 *
 * ⚠ Ohne verlässliche Zahlen ist die Antwort `false`, nicht `true`: eine Obergrenze zu
 * ERFINDEN nähme dem Entscheider den einzigen Weg, den er vielleicht noch hat. Kommt der
 * Halt trotzdem, sagt es die Meldung nach der Entscheidung (`entscheidungsMeldung`).
 */
export function ueberarbeitungAufgebraucht(
  runde: number | null | undefined,
  maxRounds: number | null | undefined,
): boolean {
  const r = brauchbareRunde(runde);
  const max = brauchbareRunde(maxRounds);
  return r !== null && max !== null && r > max;
}

/** Ist DIESE Runde die letzte vorgesehene? (Die nächste überschritte den Deckel.) */
export function letzteUeberarbeitung(
  runde: number | null | undefined,
  maxRounds: number | null | undefined,
): boolean {
  const r = brauchbareRunde(runde);
  const max = brauchbareRunde(maxRounds);
  return r !== null && max !== null && r === max;
}

/**
 * Die Meldung NACH einer Entscheidung — und die Frage, ob der Lauf danach steht.
 *
 * ⚠ Der Master wirft am Deckel NICHT. Sind die Überarbeitungen aufgebraucht (oder ist das
 * Ziel nicht mehr überarbeitbar), antwortet `decide` mit HTTP 200 und
 * `{ decision:"revise", final:true, runde, status:"blocked" }` — die Rückgabe IST verbucht,
 * nur führt sie nirgendwo hin. Ein „Erfolg"-Ton darauf ist die teuerste Falschaussage der
 * ganzen Karte: der Entscheider geht weg, der Lauf steht.
 *
 * Deshalb liest die Meldung `status` und nicht nur `decision` — und zwar bei JEDEM Ausgang:
 * auch hinter einer Ablehnung mit defektem `goto`-Ziel steht am Ende ein angehaltener Lauf.
 */
export function entscheidungsMeldung(r: {
  decision: string;
  final: boolean;
  quorum: number;
  zustimmungen: number;
  runde?: number | null;
  status?: string;
}): { text: string; angehalten: boolean } {
  const revise = r.decision === "revise";
  const kopf = revise
    ? `Zur Überarbeitung zurückgegeben${rundenZusatz(r.runde, 1)}`
    : r.decision === "reject"
      ? "Abgelehnt"
      : r.final
        ? "Freigegeben"
        : `Zustimmung erfasst (${r.zustimmungen} von ${r.quorum})`;

  if (r.status === "blocked") {
    // Zwei mögliche Ursachen (Runden aufgebraucht / Ziel nicht überarbeitbar), EIN Signal
    // auf der Leitung — der Satz nennt deshalb beide und vor allem den Ausweg. Welche es
    // war, steht als Hinweis am Lauf.
    return {
      angehalten: true,
      text: revise
        ? `${kopf} — aber der Lauf ist angehalten: die vorgesehenen Überarbeitungen sind aufgebraucht oder der Ziel-Schritt lässt sich nicht mehr überarbeiten. Jetzt braucht es eine Freigabe, eine endgültige Ablehnung oder ein erneutes Anstossen.`
        : `${kopf} — aber der Lauf ist angehalten und kommt nicht von selbst weiter. Der Grund steht beim Lauf; weiter geht es erst mit einer Entscheidung oder einem erneuten Anstossen.`,
    };
  }
  return {
    angehalten: false,
    text: r.decision === "approve" && r.final ? "Freigegeben — der Vorgang läuft weiter" : kopf,
  };
}
